// form
import { useFormContext, Controller } from "react-hook-form";
// @mui
import {
  Checkbox,
  FormLabel,
  FormGroup,
  FormControl,
  FormHelperText,
  FormControlLabel,
  FormControlLabelProps,
} from "@mui/material";

// ----------------------------------------------------------------------

interface RHFCheckboxProps extends Omit<FormControlLabelProps, "control"> {
  name: string;
  helperText?: React.ReactNode;
}

export function RHFCheckbox({ name, helperText, ...other }: RHFCheckboxProps) {
  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <div>
          <FormControlLabel control={<Checkbox {...field} checked={field.value} />} {...other} />

          {(!!error || helperText) && (
            <FormHelperText error={!!error}>{error ? error?.message : helperText}</FormHelperText>
          )}
        </div>
      )}
    />
  );
}

// ----------------------------------------------------------------------

interface RHFMultiCheckboxProps extends Omit<FormControlLabelProps, "control" | "label"> {
  name: string;
  options: string[];
  row?: boolean;
  label?: string;
  spacing?: number;
  helperText?: React.ReactNode;
}

export function RHFMultiCheckbox({
  row,
  name,
  label,
  options,
  spacing,
  helperText,
  ...other
}: RHFMultiCheckboxProps) {
  const { control } = useFormContext();

  const getSelected = (selectedItems: string[] | string, item: string) => {
    selectedItems = typeof selectedItems === "string" ? selectedItems.split(",") : selectedItems;

    return selectedItems?.includes(item)
      ? selectedItems?.filter((value) => value !== item)
      : [...(selectedItems || []), item];
  };
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => {
        return (
          <FormControl component="fieldset">
            {label && (
              <FormLabel component="legend" sx={{ typography: "body2" }}>
                {label}
              </FormLabel>
            )}

            <FormGroup
              sx={{
                ...(row && {
                  flexDirection: "row",
                }),
                "& .MuiFormControlLabel-root": {
                  "&:not(:last-of-type)": {
                    mb: spacing || 0,
                  },
                  ...(row && {
                    mr: 0,
                    "&:not(:last-of-type)": {
                      mr: spacing || 2,
                    },
                  }),
                },
              }}
            >
              {options?.map((option) => {
                return (
                  <FormControlLabel
                    key={option}
                    control={
                      <Checkbox
                        checked={field?.value?.includes(option)}
                        onChange={() => field.onChange(getSelected(field?.value, option))}
                      />
                    }
                    label={option}
                    {...other}
                  />
                );
              })}
            </FormGroup>
          </FormControl>
        );
      }}
    />
  );
}
